/**
 * Product Image Hover Zoom Effect - Matches Old Nia Mia Design
 * Shows zoomed image in product info area and dims the product details
 */

class ProductImageZoomHover extends HTMLElement {
  constructor() {
    super();
    this.zoomLevel = 2;
    this.containers = new Map();
    this.initCalled = false;
    this.zoomBox = null;
    this.productInfoContainer = null;
    this.cursorBox = null;
    this.cursorBoxWidth = null; // Will be 50% of image width, calculated dynamically
    this.cursorBoxHeight = 250; // Fixed 250px height
    this.isHovering = false; // Track hover state
    this.hideTimeout = null; // Track hide timeout
    this.lastPointer = { x: 0, y: 0 };
    this.boundPinZoomBox = this.pinZoomBox.bind(this);
    this.desktopZoomQuery = window.matchMedia('(min-width: 769px)');
  }

  connectedCallback() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.init());
    } else {
      this.init();
    }
  }

  init() {
    if (this.initCalled) return;
    this.initCalled = true;
    
    console.log('[Zoom] Initializing zoom effect (old design style)');
    
    setTimeout(() => {
      this.findProductSection();
      this.createZoomBox();
      this.setupZoom();
      this.setupMutationObserver();
      this.syncThumbnailHeight();
      this.setupResizeObserver();
      
      // Update thumbnail height on window resize
      window.addEventListener('resize', () => {
        setTimeout(() => this.syncThumbnailHeight(), 100);
      });
    }, 500);
  }

  setupResizeObserver() {
    // Watch for image size changes with ResizeObserver
    if (!('ResizeObserver' in window)) return;
    
    this.resizeObserver = new ResizeObserver(() => {
      this.syncThumbnailHeight();
    });
    
    // Observe all slideshow images
    const images = document.querySelectorAll('.slideshow img, .product-media-container img, slideshow-slide img');
    images.forEach(img => {
      this.resizeObserver.observe(img);
    });
    
    // Also observe the slideshow container
    const slideshow = document.querySelector('.slideshow, [data-slideshow]');
    if (slideshow) {
      this.resizeObserver.observe(slideshow);
    }
    
    console.log('[Zoom] ResizeObserver watching', images.length, 'images');
  }

  findProductSection() {
    // Find the main product section wrapper
    const productSection = document.querySelector('[id*="main-product"], [class*="main-product"], .product-single, section[id*="product"]');
    
    if (productSection) {
      // Add the zoom class to enable opacity transition
      productSection.classList.add('is-zoom-enabled');
      console.log('[Zoom] Added zoom class to product section');
    }
  }

  createZoomBox() {
    // Find the product details column (the right side with product info)
    this.productInfoContainer = document.querySelector('.product-details');
    
    if (this.productInfoContainer) {
      console.log('[Zoom] ✅ Found .product-details container');
      
      // Ensure relative positioning
      const currentPosition = getComputedStyle(this.productInfoContainer).position;
      if (currentPosition === 'static') {
        this.productInfoContainer.style.position = 'relative';
      }
      
      // Dim the details in place. Moving them into a new wrapper would disconnect and
      // reconnect product-form-component, variant-picker and friends, and their abort
      // signals are already spent by then, so their listeners never come back and the
      // buy button stops reacting to variant changes.
      this.zoomWrapper = this.productInfoContainer;
      this.zoomWrapper.classList.add('zoom-wrapper');
      
      // Create the zoom result box last so it paints on top of the dimmed content

      this.zoomBox = document.createElement('div');
      this.zoomBox.className = 'zoom-result-box';
      this.zoomBox.setAttribute('aria-hidden', 'true');
      this.productInfoContainer.appendChild(this.zoomBox);
      
      console.log('[Zoom] ✅ Zoom box structure created');
      console.log('[Zoom] Product details has', this.productInfoContainer.children.length, 'children');
      console.log('[Zoom] Children:', Array.from(this.productInfoContainer.children).map(c => c.className));
    } else {
      console.error('[Zoom] ❌ Could not find .product-details container!');
    }
  }

  setupZoom() {
    const selectors = [
      '.product-media img',
      '.product-media-container img',
      'media-gallery img'
    ];
    
    let images = [];
    for (const selector of selectors) {
      const found = this.querySelectorAll(selector);
      if (found.length > 0) {
        images = Array.from(found);
        console.log(`[Zoom] Found ${images.length} images`);
        break;
      }
    }
    
    if (images.length === 0) {
      console.warn('[Zoom] No product images found');
      return;
    }
    
    images.forEach((img, index) => {
      const container = img.closest('.product-media-container') || 
                       img.closest('.product-media') ||
                       img.closest('slideshow-slide') ||
                       img.parentElement;
                       
      if (!container || this.containers.has(container)) return;

      const isVideo = container.querySelector('video');
      const isModel = container.querySelector('model-viewer');
      
      if (isVideo || isModel) return;

      console.log(`[Zoom] Setting up zoom for image ${index}`);

      this.containers.set(container, { img });

      const handlers = {
        mouseenter: (e) => this.handleMouseEnter(container, img, e),
        mousemove: (e) => this.handleMouseMove(e, container, img),
        mouseleave: () => this.handleMouseLeave(container)
      };

      Object.entries(handlers).forEach(([event, handler]) => {
        container.addEventListener(event, handler);
      });

      container._zoomHandlers = handlers;
    });
    
    console.log(`[Zoom] Setup complete. ${this.containers.size} containers enabled`);
  }

  setupMutationObserver() {
    const observer = new MutationObserver(() => {
      this.setupZoom();
      this.syncThumbnailHeight(); // Sync height when content changes
    });

    observer.observe(this, {
      childList: true,
      subtree: true
    });

    this._observer = observer;
  }

  handleMouseEnter(container, img, e) {
    if (!this.zoomBox || !this.productInfoContainer) return;
    
    // Set hovering state
    this.isHovering = true;
    if (e) {
      this.lastPointer.x = e.clientX;
      this.lastPointer.y = e.clientY;
    }
    
    // Clear any pending hide timeout
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
    
    // Create cursor box immediately
    this.createCursorBox(container);
    
    if (!img.complete) {
      img.addEventListener('load', () => {
        if (this.isHovering) { // Only show if still hovering
          this.showZoomBox(container, img, e);
        }
      }, { once: true });
    } else {
      this.showZoomBox(container, img, e);
    }
    
    // Store the current container
    this.currentContainer = container;
    this.currentImage = img;
  }

  createCursorBox(container) {
    // Remove old cursor box if exists
    if (this.cursorBox && this.cursorBox.parentNode) {
      this.cursorBox.remove();
    }
    
    // Create new cursor box in the current container
    this.cursorBox = document.createElement('div');
    this.cursorBox.className = 'zoom-cursor-box';
    container.appendChild(this.cursorBox);
  }

  showZoomBox(container, img, e) {
    if (!this.zoomBox || !this.productInfoContainer || !this.isHovering) {
      console.error('[Zoom] Cannot show zoom box - missing elements or not hovering');
      return;
    }
    
    const imgSrc = img.getAttribute('data-zoom-src') || img.src;
    
    console.log('[Zoom] Showing zoom with image:', imgSrc);
    
    // Get the image container dimensions
    const containerRect = container.getBoundingClientRect();
    const productDetailsRect = this.productInfoContainer.getBoundingClientRect();
    
    // Calculate cursor box width as 50% of image width
    this.cursorBoxWidth = containerRect.width * 0.5;
    
    // Calculate the top position relative to product-details
    const topOffset = containerRect.top - productDetailsRect.top;
    
    // Set zoom box position
    this.zoomBox.style.top = topOffset + 'px';
    if (e) {
      this.lastPointer.x = e.clientX;
      this.lastPointer.y = e.clientY;
    }
    this.productInfoContainer.classList.add('is-zoom-open');
    this.pinZoomBox();
    this.bindZoomScroll();
    
    // Set the background image
    this.zoomBox.style.backgroundImage = `url('${imgSrc}')`;
    this.zoomBox.style.display = 'block';
    
    // Reset transform to start big, then animate to normal
    this.zoomBox.style.transform = 'scale(1.1)';
    this.zoomBox.style.opacity = '0';
    
    // Trigger animation by setting to normal scale after a frame
    requestAnimationFrame(() => {
      if (this.isHovering) { // Only animate if still hovering
        this.zoomBox.style.visibility = 'visible';
        this.zoomBox.style.opacity = '1';
        this.zoomBox.style.transform = 'scale(1)';
      }
    });
    
    // Store container rect for later calculations
    this.currentContainerRect = containerRect;
    
    // Dim the product info content wrapper (not the zoom box)
    if (this.zoomWrapper) {
      this.zoomWrapper.classList.add('zoom-dimmed');
    }
    
    container.classList.add('zoom-active');
    
    // Position cursor box immediately and update zoom
    if (e) {
      const x = e.clientX - containerRect.left;
      const y = e.clientY - containerRect.top;
      this.updateCursorBox(x, y, container);
      this.updateZoomBackground(x, y, containerRect);
    }
    
    console.log('[Zoom] ✅ Zoom box visible with animation');
  }

  isDesktopZoom() {
    return this.desktopZoomQuery ? this.desktopZoomQuery.matches : window.innerWidth > 768;
  }

  getStickyTopOffset() {
    if (!this.productInfoContainer) return 48;

    const stickyValue = getComputedStyle(this.productInfoContainer)
      .getPropertyValue('--sticky-header-offset')
      .trim();
    const stickyPx = parseFloat(stickyValue);

    if (!Number.isNaN(stickyPx) && stickyPx > 0) return stickyPx;

    const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return 3 * rootFontSize;
  }

  bindZoomScroll() {
    this.unbindZoomScroll();
    if (!this.isDesktopZoom()) return;

    window.addEventListener('scroll', this.boundPinZoomBox, { passive: true });
    window.addEventListener('resize', this.boundPinZoomBox);
  }

  unbindZoomScroll() {
    window.removeEventListener('scroll', this.boundPinZoomBox);
    window.removeEventListener('resize', this.boundPinZoomBox);
  }

  pinZoomBox() {
    if (!this.zoomBox || !this.productInfoContainer || !this.isHovering) return;

    if (!this.isDesktopZoom()) {
      this.zoomBox.classList.remove('zoom-result-box--stuck');
      this.zoomBox.style.removeProperty('--zoom-box-top');
      this.zoomBox.style.removeProperty('--zoom-box-left');
      this.zoomBox.style.removeProperty('--zoom-box-width');
      return;
    }

    const detailsRect = this.productInfoContainer.getBoundingClientRect();
    const stickyTop = this.getStickyTopOffset();
    const top = Math.max(stickyTop, detailsRect.top);

    const width = Math.min(detailsRect.width * 0.85, 450);

    this.zoomBox.classList.add('zoom-result-box--stuck');
    this.zoomBox.style.setProperty('--zoom-box-top', `${top}px`);
    this.zoomBox.style.setProperty('--zoom-box-left', `${detailsRect.left}px`);
    this.zoomBox.style.setProperty('--zoom-box-width', `${width}px`);

    if (this.currentContainer && this.zoomBox.style.display === 'block') {
      const containerRect = this.currentContainer.getBoundingClientRect();
      this.currentContainerRect = containerRect;
      const x = this.lastPointer.x - containerRect.left;
      const y = this.lastPointer.y - containerRect.top;
      this.updateCursorBox(x, y, this.currentContainer);
      this.updateZoomBackground(x, y, containerRect);
    }
  }

  unpinZoomBox() {
    this.unbindZoomScroll();

    if (this.productInfoContainer) {
      this.productInfoContainer.classList.remove('is-zoom-open');
    }

    if (!this.zoomBox) return;

    this.zoomBox.classList.remove('zoom-result-box--stuck');
    this.zoomBox.style.removeProperty('--zoom-box-top');
    this.zoomBox.style.removeProperty('--zoom-box-left');
    this.zoomBox.style.removeProperty('--zoom-box-width');
  }

  handleMouseMove(e, container, img) {
    if (!this.zoomBox || this.zoomBox.style.display !== 'block') return;

    this.lastPointer.x = e.clientX;
    this.lastPointer.y = e.clientY;
    
    const containerRect = container.getBoundingClientRect();
    
    let x = e.clientX - containerRect.left;
    let y = e.clientY - containerRect.top;

    // Update cursor box position
    this.updateCursorBox(x, y, container);
    
    // Update zoom background to show cursor box area
    this.updateZoomBackground(x, y, containerRect);
  }

  updateZoomBackground(x, y, containerRect) {
    if (!this.zoomBox) return;
    
    // Cursor box is 50% width × 250px height, centered on cursor
    const halfWidth = this.cursorBoxWidth / 2;
    const halfHeight = this.cursorBoxHeight / 2;
    
    // Calculate the cursor box boundaries
    let boxLeft = x - halfWidth;
    let boxTop = y - halfHeight;
    
    // Constrain to image bounds
    boxLeft = Math.max(0, Math.min(boxLeft, containerRect.width - this.cursorBoxWidth));
    boxTop = Math.max(0, Math.min(boxTop, containerRect.height - this.cursorBoxHeight));
    
    // Get zoom box dimensions
    const zoomBoxWidth = this.zoomBox.offsetWidth;
    const zoomBoxHeight = this.zoomBox.offsetHeight;
    
    // Calculate scale factor: how much bigger is zoom box compared to cursor box
    const scaleX = zoomBoxWidth / this.cursorBoxWidth;
    const scaleY = zoomBoxHeight / this.cursorBoxHeight;
    
    // Size the background: original image scaled up by the scale factor
    const bgWidth = containerRect.width * scaleX;
    const bgHeight = containerRect.height * scaleY;
    
    // Position the background: 
    // We want cursor box top-left (boxLeft, boxTop) to appear at zoom box top-left (0, 0)
    // So we offset the background by -(boxLeft * scaleX), -(boxTop * scaleY)
    const bgPosX = -(boxLeft * scaleX);
    const bgPosY = -(boxTop * scaleY);
    
    this.zoomBox.style.backgroundSize = `${bgWidth}px ${bgHeight}px`;
    this.zoomBox.style.backgroundPosition = `${bgPosX}px ${bgPosY}px`;
    this.zoomBox.style.backgroundRepeat = 'no-repeat';
  }

  updateCursorBox(x, y, container) {
    if (!this.cursorBox) return;
    
    // Cursor box is 50% width × 250px height
    const halfWidth = this.cursorBoxWidth / 2;
    const halfHeight = this.cursorBoxHeight / 2;
    
    // Center the box on cursor
    const left = x - halfWidth;
    const top = y - halfHeight;
    
    // Get container bounds
    const containerRect = container.getBoundingClientRect();
    
    // Constrain to container bounds
    const maxLeft = containerRect.width - this.cursorBoxWidth;
    const maxTop = containerRect.height - this.cursorBoxHeight;
    
    const constrainedLeft = Math.max(0, Math.min(left, maxLeft));
    const constrainedTop = Math.max(0, Math.min(top, maxTop));
    
    this.cursorBox.style.left = constrainedLeft + 'px';
    this.cursorBox.style.top = constrainedTop + 'px';
    this.cursorBox.style.width = this.cursorBoxWidth + 'px';
    this.cursorBox.style.height = this.cursorBoxHeight + 'px';
    this.cursorBox.style.display = 'block';
  }

  handleMouseLeave(container) {
    // Set hovering state to false
    this.isHovering = false;
    this.unpinZoomBox();
    
    if (this.zoomBox) {
      // Animate out: scale down and fade out
      this.zoomBox.style.opacity = '0';
      this.zoomBox.style.transform = 'scale(0.85)'; // Shrink smaller than starting size
      
      // Hide display after transition completes
      this.hideTimeout = setTimeout(() => {
        if (!this.isHovering && this.zoomBox) { // Only hide if still not hovering
          this.zoomBox.style.display = 'none';
          this.zoomBox.style.visibility = 'hidden';
        }
      }, 300); // Match CSS transition duration
    }
    
    // Hide and remove cursor box immediately
    if (this.cursorBox) {
      this.cursorBox.style.display = 'none';
      if (this.cursorBox.parentNode) {
        this.cursorBox.remove();
      }
      this.cursorBox = null;
    }
    
    // Remove dim effect from product info wrapper
    if (this.zoomWrapper) {
      this.zoomWrapper.classList.remove('zoom-dimmed');
    }
    
    // Remove active class from the specific container
    if (container) {
      container.classList.remove('zoom-active');
    }
    
    this.currentContainer = null;
    this.currentImage = null;
  }

  syncThumbnailHeight() {
    // Find all possible main image selectors
    const mainImageSelectors = [
      '.slideshow__slide.is-selected img',
      'slideshow-slide[aria-hidden="false"] img',
      '.product-media-container img',
      '.slideshow img',
      '.product-media img'
    ];
    
    let mainImage = null;
    for (const selector of mainImageSelectors) {
      mainImage = document.querySelector(selector);
      if (mainImage && mainImage.offsetHeight > 0) break;
    }
    
    if (!mainImage || mainImage.offsetHeight === 0) {
      console.log('[Zoom] No visible main image found, retrying...');
      setTimeout(() => this.syncThumbnailHeight(), 200);
      return;
    }
    
    // Wait for image to load
    if (!mainImage.complete) {
      mainImage.addEventListener('load', () => this.syncThumbnailHeight(), { once: true });
      return;
    }
    
    // Get the actual rendered height of the main image
    const imageHeight = mainImage.offsetHeight;
    
    console.log('[Zoom] Found main image with height:', imageHeight + 'px');
    
    // Find and update thumbnail container
    const thumbnailContainer = document.querySelector('.slideshow-controls__thumbnails');
    if (thumbnailContainer) {
      thumbnailContainer.style.height = imageHeight + 'px !important';
      thumbnailContainer.style.minHeight = imageHeight + 'px';
      thumbnailContainer.style.maxHeight = imageHeight + 'px';
      console.log('[Zoom] ✅ Set thumbnail container height to:', imageHeight + 'px');
    }
    
    // Find the main slideshow/carousel container
    const slideshowContainer = document.querySelector('.product-media-gallery__carousel, [data-slideshow], .slideshow');
    if (slideshowContainer) {
      slideshowContainer.style.height = imageHeight + 'px';
      slideshowContainer.style.minHeight = imageHeight + 'px';
      slideshowContainer.style.maxHeight = imageHeight + 'px';
      console.log('[Zoom] ✅ Set slideshow container height to:', imageHeight + 'px');
    }
    
    // Position arrow buttons vertically centered on the image
    const arrowButtons = document.querySelectorAll('.slideshow__arrow');
    if (arrowButtons.length > 0) {
      arrowButtons.forEach(arrow => {
        arrow.style.position = 'absolute';
        arrow.style.top = '50%';
        arrow.style.transform = 'translateY(-50%)';
      });
      console.log('[Zoom] ✅ Centered', arrowButtons.length, 'arrow buttons');
    }
  }

  disconnectedCallback() {
    this.containers.forEach((data, container) => {
      if (container._zoomHandlers) {
        Object.entries(container._zoomHandlers).forEach(([event, handler]) => {
          container.removeEventListener(event, handler);
        });
      }
    });
    this.containers.clear();

    if (this._observer) {
      this._observer.disconnect();
    }

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }

    if (this.cursorBox && this.cursorBox.parentNode) {
      this.cursorBox.remove();
    }

    if (this.zoomBox && this.zoomBox.parentNode) {
      this.zoomBox.remove();
    }

    if (this.zoomWrapper) {
      this.zoomWrapper.classList.remove('zoom-wrapper', 'zoom-dimmed');
    }

    this.unbindZoomScroll();
  }
}

if (!customElements.get('product-image-zoom-hover')) {
  customElements.define('product-image-zoom-hover', ProductImageZoomHover);
}
